/**
 * Hero Layout Preset Picker Component
 * 
 * Displays a grid of preset layouts with visual icons.
 * Supports keyboard navigation and selection.
 */

import React, { useRef, useEffect } from 'react';
import { PRESETS as FALLBACK_PRESETS } from './heroLayoutPresets';
import { PresetIcon } from './HeroLayoutIcons';
import HeroLayoutLegend from './HeroLayoutLegend';
import { layoutsEqual } from '../../utils/heroLayoutUtils';
import { surfaces, borders, textColors, typography, interactive } from '../../../design-system/tokens';

/**
 * @param {Object} props
 * @param {string} props.value - Current layout value (JSON string)
 * @param {string} props.selectedPresetId - ID of the currently selected preset
 * @param {Function} props.onChange - Callback when preset selected (preset) => void
 * @param {boolean} props.disabled - Whether picker is disabled
 * @param {number} [props.columns] - Explicit number of columns to render (overrides responsive classes)
 * @param {Array} [props.presets] - Optional presets array (from backend); falls back to local PRESETS
 */
export default function HeroLayoutPresetPicker({ value, selectedPresetId, onChange, disabled = false, columns, presets }) {
  // Use backend presets if provided *and non-empty*, otherwise fallback to local.
  // Empty arrays are truthy in JS, so an explicit length check is needed.
  const PRESETS = (Array.isArray(presets) && presets.length > 0 ? presets : FALLBACK_PRESETS)
    .filter((p) => p?.id !== 'custom');
  const gridRef = useRef(null);

  // Focus management for keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (disabled) return;

      const cards = gridRef.current?.querySelectorAll('[role="button"]');
      if (!cards || cards.length === 0) return;

      const currentIndex = Array.from(cards).findIndex(card => card === document.activeElement);
      
      let nextIndex = -1;
      const columnCount = Number(columns) > 0 ? Number(columns) : 3; // default to 3 when unknown

      switch (e.key) {
        case 'ArrowRight':
          nextIndex = currentIndex < cards.length - 1 ? currentIndex + 1 : 0;
          e.preventDefault();
          break;
        case 'ArrowLeft':
          nextIndex = currentIndex > 0 ? currentIndex - 1 : cards.length - 1;
          e.preventDefault();
          break;
        case 'ArrowDown':
          nextIndex = currentIndex + columnCount < cards.length ? currentIndex + columnCount : currentIndex;
          e.preventDefault();
          break;
        case 'ArrowUp':
          nextIndex = currentIndex - columnCount >= 0 ? currentIndex - columnCount : currentIndex;
          e.preventDefault();
          break;
        default:
          return;
      }

      if (nextIndex >= 0 && cards[nextIndex]) {
        cards[nextIndex].focus();
      }
    };

    const grid = gridRef.current;
    grid?.addEventListener('keydown', handleKeyDown);
    
    return () => {
      grid?.removeEventListener('keydown', handleKeyDown);
    };
  }, [disabled]);

  const handlePresetClick = (preset) => {
    if (disabled) return;
    onChange?.(preset);
  };

  const handleKeyPress = (e, preset) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handlePresetClick(preset);
    }
  };

  return (
    <div className="hero-layout-preset-picker">
      {/* Legend showing what colors mean */}
      <HeroLayoutLegend compact={true} />
      
      {/* Preset Grid */}
      <div 
        ref={gridRef}
        className="grid gap-3"
        role="radiogroup"
        aria-label="Layout presets"
        style={columns && Number(columns) > 0 
          ? { gridTemplateColumns: `repeat(${Math.min(5, Math.max(1, Number(columns)))}, minmax(0, 1fr))` }
          : { gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' } // sensible default based on card size
        }
      >
        {PRESETS.map((preset) => {
          // Compare by preset ID instead of layout value to distinguish between presets with same structure but different item types
          const isSelected = selectedPresetId ? preset.id === selectedPresetId : (preset.value && value && layoutsEqual(preset.value, value));

          return (
            <div
              key={preset.id}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-pressed={isSelected}
              aria-label={`${preset.label}: ${preset.description}`}
              title={preset.description}
              className={`
                group relative p-3 rounded-lg border-2 transition-all cursor-pointer
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                ${isSelected 
                  ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-md` 
                  : `${borders.default} ${surfaces.secondary} ${surfaces.hover} hover:shadow-sm`
                }
                focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-1
              `}
              onClick={() => handlePresetClick(preset)}
              onKeyPress={(e) => handleKeyPress(e, preset)}
            >
              {/* Icon */}
              <div className="flex justify-center mb-2">
                <PresetIcon 
                  columnSequences={preset.columnSequences} 
                  width={80} 
                  height={60} 
                />
              </div>

              {/* Label */}
              <div className={`text-center ${typography.bodySmall} font-medium ${isSelected ? "text-white" : textColors.primary}`}>
                {preset.label}
              </div>

              {/* Description subtitle */}
              {preset.description && (
                <div className={`text-center ${typography.caption} ${isSelected ? "text-white/70" : textColors.tertiary} mt-1 px-1 leading-tight`}>
                  {preset.description}
                </div>
              )}

              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <svg 
                    className="w-5 h-5 text-white" 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                  >
                    <path 
                      fillRule="evenodd" 
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
                      clipRule="evenodd" 
                    />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

